import { Head, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"

interface JobCost {
  id: number
  job_type: string
  job_id: string | null
  status: string
  provider: string
  model: string | null
  input_tokens: number | null
  output_tokens: number | null
  characters: number | null
  cost_cents: number | null
  cost_dollars: number | null
  duration_ms: number | null
  metadata: Record<string, unknown>
  costable_type: string
  costable_id: number
  costable_name: string | null
  account_name: string
  user_name: string
  started_at: string
  finished_at: string | null
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Summary {
  total_cost_cents: number
  total_jobs: number
  avg_duration_ms: number | null
  failed_count: number
  by_provider: Record<string, number>
  by_job_type: Record<string, number>
  by_video: Array<{ name: string; cost_cents: number }>
  by_listing: Array<{ name: string; cost_cents: number }>
}

interface DailyCost {
  date: string
  cost_cents: number
}

interface Props {
  search: string | null
  sort: string
  direction: string
  job_type: string | null
  provider: string | null
  period: string
  costs: JobCost[]
  pagination: PaginationMeta
  summary: Summary
  daily_costs: DailyCost[]
}

const PROVIDER_COLOURS: Record<string, string> = {
  anthropic: "#D4A853",
  elevenlabs: "#60A5FA",
  fal: "#34D399",
  remotion: "#A78BFA",
  aws: "#F97316",
  kernel: "#F472B6",
  internal: "#6B6B6B",
}

const JOB_TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "scrape", label: "Scrape" },
  { value: "extract", label: "Extract" },
  { value: "classify_images", label: "Classify images" },
  { value: "script", label: "Script" },
  { value: "voiceover", label: "Voiceover" },
  { value: "music", label: "Music" },
  { value: "enhance_image", label: "Enhance image" },
  { value: "animate_clip", label: "Animate clip" },
  { value: "render", label: "Render" },
  { value: "copy_video", label: "Copy video" },
] as const

const PROVIDER_OPTIONS = [
  { value: "all", label: "All providers" },
  { value: "anthropic", label: "Anthropic" },
  { value: "elevenlabs", label: "ElevenLabs" },
  { value: "fal", label: "fal.ai" },
  { value: "remotion", label: "Remotion" },
  { value: "aws", label: "AWS" },
  { value: "kernel", label: "Kernel" },
  { value: "internal", label: "Internal" },
] as const

const PERIOD_OPTIONS = [
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" },
  { value: "all", label: "All time" },
] as const

const COLUMNS = [
  { key: "created_at", label: "Date", sortable: true },
  { key: "job_type", label: "Job Type", sortable: true },
  { key: "provider", label: "Provider", sortable: true },
  { key: "costable", label: "Costable", sortable: false },
  { key: "model", label: "Model", sortable: false },
  { key: "tokens", label: "Tokens", sortable: false },
  { key: "characters", label: "Chars", sortable: false },
  { key: "cost_cents", label: "Cost", sortable: true },
  { key: "duration_ms", label: "Duration", sortable: true },
  { key: "account", label: "Account", sortable: false },
  { key: "user", label: "User", sortable: false },
] as const

export default function AdminCosts({
  search,
  sort,
  direction,
  job_type,
  provider,
  period,
  costs,
  pagination,
  summary,
  daily_costs,
}: Props) {
  const [searchValue, setSearchValue] = useState(search || "")

  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      if (search) p.search = search
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      if (job_type) p.job_type = job_type
      if (provider) p.provider = provider
      if (period !== "month") p.period = period
      const filtered = Object.entries(overrides).filter(
        (entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== "",
      )
      return { ...p, ...Object.fromEntries(filtered) }
    },
    [search, sort, direction, job_type, provider, period],
  )

  function navigate(overrides: Record<string, string | undefined>) {
    router.get("/admin/costs", buildParams({ ...overrides, page: undefined }), {
      preserveState: true,
      preserveScroll: true,
    })
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    navigate({ search: searchValue || undefined })
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    navigate({ sort: column, direction: newDirection })
  }

  const failureRate =
    summary.total_jobs > 0
      ? ((summary.failed_count / summary.total_jobs) * 100).toFixed(1)
      : "0.0"

  return (
    <AdminLayout>
      <Head title="Costs | Admin" />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-semibold text-cream">Costs</h1>
            <p className="mt-1 text-sm text-cream/50">API spend and job cost tracking</p>
          </div>

          <Select value={period} onValueChange={(v) => navigate({ period: v })}>
            <SelectTrigger className="h-9 w-[140px] border-white/10 bg-white/5 text-sm text-cream">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dark border-white/10 bg-charcoal">
              {PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary cards */}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            label="Total spend"
            value={formatCurrency(summary.total_cost_cents)}
            accent
          />
          <SummaryCard label="Total jobs" value={summary.total_jobs.toLocaleString()} />
          <SummaryCard
            label="Avg duration"
            value={summary.avg_duration_ms ? formatDuration(summary.avg_duration_ms) : "-"}
          />
          <SummaryCard
            label="Failure rate"
            value={`${failureRate}%`}
            alert={summary.failed_count > 0}
          />
        </div>

        {/* Daily cost chart */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
          <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-cream/40">
            30-day spend
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily_costs} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="rgba(245,242,237,0.04)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "rgba(245,242,237,0.3)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: "rgba(245,242,237,0.3)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => formatCurrency(v)}
                />
                <Tooltip content={<CostTooltip />} cursor={{ fill: "rgba(245,242,237,0.03)" }} />
                <Bar dataKey="cost_cents" fill="#C41E2A" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Breakdown cards */}
        <div className="grid gap-3 lg:grid-cols-2">
          <BreakdownCard title="By provider" data={summary.by_provider} />
          <BreakdownCard title="By job type" data={summary.by_job_type} />
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <ListBreakdownCard title="By video" items={summary.by_video} />
          <ListBreakdownCard title="By listing" items={summary.by_listing} />
        </div>

        {/* Filters + table */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-medium uppercase tracking-wider text-cream/40">
              Job costs
              <span className="ml-2 text-cream/25">({pagination.count})</span>
            </h2>

            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={job_type || "all"}
                onValueChange={(v) => navigate({ job_type: v === "all" ? undefined : v })}
              >
                <SelectTrigger className="h-8 w-[140px] border-white/10 bg-white/5 text-xs text-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark border-white/10 bg-charcoal">
                  {JOB_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={provider || "all"}
                onValueChange={(v) => navigate({ provider: v === "all" ? undefined : v })}
              >
                <SelectTrigger className="h-8 w-[130px] border-white/10 bg-white/5 text-xs text-cream">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="dark border-white/10 bg-charcoal">
                  {PROVIDER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <form onSubmit={handleSearch} className="flex gap-1.5">
                <div className="relative">
                  <SearchIcon className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-cream/30" />
                  <Input
                    placeholder="Account or user..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    className="h-8 w-44 border-white/10 bg-white/5 pl-7 text-xs text-cream placeholder:text-cream/30"
                  />
                </div>
                {search && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-cream/50 hover:text-cream"
                    onClick={() => {
                      setSearchValue("")
                      navigate({ search: undefined })
                    }}
                  >
                    Clear
                  </Button>
                )}
              </form>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-md border border-white/[0.06]">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-cream/40"
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 transition-colors hover:text-cream/70"
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          <SortIcon
                            active={sort === col.key}
                            direction={sort === col.key ? direction : undefined}
                          />
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {costs.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-sm text-cream/30">
                      {search ? `No costs matching "${search}"` : "No cost records found"}
                    </td>
                  </tr>
                )}
                {costs.map((cost) => (
                  <tr
                    key={cost.id}
                    className="border-b border-white/[0.04] text-cream/60"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-cream/40">
                      {formatRelative(cost.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Badge
                        variant="outline"
                        className="text-[10px] border-white/10 text-cream/60"
                      >
                        {cost.job_type.replace("_", " ")}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium"
                        style={{
                          backgroundColor: `${PROVIDER_COLOURS[cost.provider] || "#6B6B6B"}15`,
                          color: PROVIDER_COLOURS[cost.provider] || "#6B6B6B",
                        }}
                      >
                        {cost.provider}
                      </span>
                    </td>
                    <td className="max-w-[120px] truncate whitespace-nowrap px-3 py-2 text-xs text-cream/40">
                      {cost.costable_name || "-"}
                    </td>
                    <td className="max-w-[120px] truncate whitespace-nowrap px-3 py-2 text-xs text-cream/30">
                      {cost.model || "-"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-cream/40">
                      {cost.input_tokens != null
                        ? `${cost.input_tokens.toLocaleString()} / ${cost.output_tokens?.toLocaleString()}`
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-cream/40">
                      {cost.characters?.toLocaleString() ?? "-"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-cream/70">
                      {cost.cost_cents != null ? formatCurrency(cost.cost_cents) : "-"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-cream/40">
                      {cost.duration_ms != null ? formatDuration(cost.duration_ms) : "-"}
                    </td>
                    <td className="max-w-[120px] truncate whitespace-nowrap px-3 py-2 text-xs text-cream/40">
                      {cost.account_name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-cream/40">
                      {cost.user_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="mt-3">
              <Pagination
                baseUrl="/admin/costs"
                params={buildParams()}
                pagination={pagination}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

// --- Sub-components ---

function SummaryCard({
  label,
  value,
  accent,
  alert,
}: {
  label: string
  value: string
  accent?: boolean
  alert?: boolean
}) {
  return (
    <div
      className={`group relative overflow-hidden rounded-lg border p-5 ${
        alert
          ? "border-red/20 bg-red/5"
          : "border-white/[0.06] bg-white/[0.03]"
      }`}
    >
      <p className="text-xs font-medium uppercase tracking-wider text-cream/40">{label}</p>
      <p className={`mt-2 text-2xl font-semibold tabular-nums ${accent ? "text-cream" : alert ? "text-red" : "text-cream/80"}`}>
        {value}
      </p>
      <div className="pointer-events-none absolute -right-3 -top-3 size-16 rounded-full bg-white/[0.02]" />
    </div>
  )
}

function BreakdownCard({
  title,
  data,
}: {
  title: string
  data: Record<string, number>
}) {
  const entries = Object.entries(data)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)

  const total = entries.reduce((sum, e) => sum + e.value, 0)

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
      <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-cream/40">
        {title}
      </h2>
      {entries.length === 0 ? (
        <p className="py-4 text-center text-sm text-cream/30">No data</p>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const pct = total > 0 ? (entry.value / total) * 100 : 0
            const colour = PROVIDER_COLOURS[entry.name] || "#6B6B6B"
            return (
              <div key={entry.name}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-cream/60">{formatLabel(entry.name)}</span>
                  <span className="tabular-nums text-cream/40">{formatCurrency(entry.value)}</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: colour }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function ListBreakdownCard({
  title,
  items,
}: {
  title: string
  items: Array<{ name: string; cost_cents: number }>
}) {
  const total = items.reduce((sum, e) => sum + e.cost_cents, 0)

  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5">
      <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-cream/40">
        {title}
      </h2>
      {items.length === 0 ? (
        <p className="py-4 text-center text-sm text-cream/30">No data</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const pct = total > 0 ? (item.cost_cents / total) * 100 : 0
            return (
              <div key={item.name}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="max-w-[70%] truncate text-cream/60">{item.name}</span>
                  <span className="tabular-nums text-cream/40">
                    {formatCurrency(item.cost_cents)}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                  <div
                    className="h-full rounded-full bg-red transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CostTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-md border border-white/10 bg-charcoal/95 px-3 py-2 shadow-xl backdrop-blur-sm">
      {label && <p className="mb-1 text-[11px] font-medium text-cream/50">{label}</p>}
      <p className="text-sm tabular-nums text-cream">{formatCurrency(payload[0].value)}</p>
    </div>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg className="size-3 opacity-30" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}

// --- Helpers ---

function formatCurrency(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1) return `$${dollars.toFixed(2)}`
  return `${cents}c`
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const seconds = ms / 1000
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const minutes = Math.floor(seconds / 60)
  const remaining = Math.round(seconds % 60)
  return `${minutes}m ${remaining}s`
}

function formatRelative(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "now"
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" })
}

function formatLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}
